import { useState, useEffect } from 'react';
import Joyride, { Step, CallBackProps, STATUS, ACTIONS } from 'react-joyride';
import { useLocation } from 'react-router-dom';

const TOUR_VERSION = '2.0.0'; // Increment to re-trigger tour for all users

export function OnboardingTour() {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const storedVersion = localStorage.getItem('sentinel-tour-version');
    const hasSeenCurrentTour = storedVersion === TOUR_VERSION;
    
    // Only show tour on inbox (main dashboard) and if not seen
    if (!hasSeenCurrentTour && location.pathname === '/inbox') {
      // Verify that tour targets exist before starting
      const checkTargetsExist = () => {
        const requiredTargets = ['[data-tour="inbox"]', '[data-tour="sentinel"]'];
        const allExist = requiredTargets.every(selector => document.querySelector(selector));
        
        if (allExist) {
          setRun(true);
        } else {
          // Retry after a short delay if elements aren't loaded yet
          setTimeout(checkTargetsExist, 500);
        }
      };
      
      // Delay to ensure DOM elements are loaded
      setTimeout(checkTargetsExist, 1000);
    }
  }, [location]);

  const steps: Step[] = [
    {
      target: 'body',
      content: (
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-foreground">Welcome to Sentinel!</h3>
          <p className="text-muted-foreground">
            Let's take a quick tour of your AI-powered financial intelligence platform. 
            This will only take 60 seconds.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="inbox"]',
      content: (
        <div className="space-y-2">
          <h4 className="font-bold text-foreground">Action Inbox</h4>
          <p className="text-sm text-muted-foreground">
            Your command center for alerts, approvals, and high-priority items that need your attention.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="exceptions"]',
      content: (
        <div className="space-y-2">
          <h4 className="font-bold text-foreground">Exceptions Queue</h4>
          <p className="text-sm text-muted-foreground">
            Resolve unmatched transactions, flagged invoices, and items that need manual review.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="sentinel"]',
      content: (
        <div className="space-y-2">
          <h4 className="font-bold text-foreground">Sentinel AI</h4>
          <p className="text-sm text-muted-foreground">
            Your autonomous finance guardian. Chat naturally to analyze data, run reports, and get insights.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="runway"]',
      content: (
        <div className="space-y-2">
          <h4 className="font-bold text-foreground">Runway Forecasting</h4>
          <p className="text-sm text-muted-foreground">
            30-day cash flow predictions with scenario modeling. See your financial future clearly.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="settings"]',
      content: (
        <div className="space-y-2">
          <h4 className="font-bold text-foreground">Settings & Autopilot</h4>
          <p className="text-sm text-muted-foreground">
            Configure notifications, matching thresholds, and AI autonomy levels for your workflow.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: 'body',
      content: (
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-foreground">You're All Set!</h3>
          <p className="text-muted-foreground">
            Start by checking your Inbox for pending items, or chat with Sentinel AI for instant insights.
          </p>
          <p className="text-sm text-primary font-semibold">
            Pro tip: Use voice input with Sentinel for hands-free operation!
          </p>
        </div>
      ),
      placement: 'center',
    },
  ];

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, action, index } = data;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      localStorage.setItem('sentinel-tour-version', TOUR_VERSION);
    }

    if (action === ACTIONS.NEXT || action === ACTIONS.PREV) {
      setStepIndex(index + (action === ACTIONS.NEXT ? 1 : -1));
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: 'hsl(186 100% 50%)',
          backgroundColor: 'hsl(217 33% 11%)',
          textColor: 'hsl(210 40% 98%)',
          overlayColor: 'rgba(0, 0, 0, 0.7)',
          arrowColor: 'hsl(217 33% 11%)',
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: '1.5rem',
          padding: '1.5rem',
          fontSize: '0.95rem',
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        buttonNext: {
          backgroundColor: 'hsl(186 100% 50%)',
          borderRadius: '0.75rem',
          padding: '0.5rem 1.5rem',
          fontSize: '0.875rem',
          fontWeight: '600',
        },
        buttonBack: {
          color: 'hsl(215 20% 65%)',
          marginRight: '0.5rem',
        },
        buttonSkip: {
          color: 'hsl(215 20% 65%)',
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Get Started',
        next: 'Next',
        skip: 'Skip Tour',
      }}
    />
  );
}

// Export function to reset tour (for Settings page)
export function resetOnboardingTour() {
  localStorage.removeItem('sentinel-tour-version');
}
